import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const assetUrl = (url) => url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null;

/**
 * Open Golf 2026 — post-checkout success page. Polls /api/payments/status
 * until Stripe confirms the session is paid, then renders a "You're In!"
 * card with a back-to-leaderboard link. Same polling pattern as the WC
 * SuccessPage so we get consistent UX while we await webhook confirmation.
 */
export default function OpenGolfSuccessPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('checking');
  const [club, setClub] = useState(null);
  const [polls, setPolls] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/golf/clubs/${slug}`)
      .then((r) => { if (!cancelled) setClub(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    if (polls >= 10) { setStatus('timeout'); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.status === 'paid') { setStatus('paid'); return; }
        setTimeout(() => { if (!cancelled) setPolls((p) => p + 1); }, 2000);
      } catch {
        if (!cancelled) setTimeout(() => setPolls((p) => p + 1), 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, polls]);

  const primary = club?.primary_color || '#1D3557';

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#fafafa' }}>
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="opengolf-success-card">
        {club && (
          <div className="flex items-center justify-center gap-2 mb-4">
            {club.logo_url ? (
              <img src={assetUrl(club.logo_url)} alt={club.name} className="w-8 h-8 object-contain" />
            ) : (
              <div className="w-8 h-8 flex items-center justify-center font-bold rounded text-white text-sm" style={{ background: primary }}>{club.name.charAt(0)}</div>
            )}
            <span className="text-sm font-bold" style={{ color: primary }}>{club.name}</span>
          </div>
        )}

        {status === 'checking' && (
          <>
            <div className="text-4xl mb-3">⏳</div>
            <h1 className="text-2xl font-black text-[#1D3557] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Confirming payment…</h1>
            <p className="text-sm text-gray-600">This usually takes a few seconds.</p>
          </>
        )}

        {status === 'paid' && (
          <>
            <div className="text-5xl mb-3">⛳</div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#E63946] mb-2">Entry Confirmed</p>
            <h1 className="text-3xl font-black text-[#1D3557] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>You&apos;re In!</h1>
            <p className="text-sm text-gray-600 mb-5">We&apos;ve emailed your 4-golfer ticket. Good luck — Royal Birkdale awaits.</p>
            <a href={`/opengolf2026/${slug}`} className="inline-block px-6 py-3 text-sm font-bold uppercase tracking-widest text-white rounded-lg" style={{ background: primary }} data-testid="success-back-link">
              Back to predictor
            </a>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="text-4xl mb-3">📬</div>
            <h1 className="text-xl font-black text-[#1D3557] mb-2">Still confirming…</h1>
            <p className="text-sm text-gray-600 mb-4">Your card was charged. If you don&apos;t receive a confirmation email in 10 minutes, contact your club.</p>
            <a href={`/opengolf2026/${slug}`} className="inline-block text-sm font-bold underline" style={{ color: primary }}>Back to predictor</a>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-4xl mb-3">⚠️</div>
            <h1 className="text-xl font-black text-[#1D3557] mb-2">Missing session</h1>
            <p className="text-sm text-gray-600">We couldn&apos;t find a payment session in the URL. Try returning to the predictor.</p>
          </>
        )}
      </div>
    </div>
  );
}
